import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Pagination,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import AiLoader from '@product-framework/components/ArtificalIntelligence/AiLoader';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { toLongDate } from '@utils/Dated';

function FromChip({ from, config, properName }) {
  return (
    <Box>
      From: {config[from].text}{' '}
      {properName && (
        <strong style={{ color: '#000000', ml: 1 }}>({properName})</strong>
      )}
    </Box>
  );
}

function UserAvatar({ from, config, initials }) {
  const theme = useTheme();
  return (
    <Box sx={{ mx: 1 }}>
      <Avatar
        sx={{
          fontSize: '18px',
          bgcolor: config[from].color || theme.palette.secondary.main,
        }}>
        {initials}
      </Avatar>
    </Box>
  );
}

function Conversation({
  status = null,
  message = null,
  type = null,
  from = null,
  to = null,
  createdTime = null,
  firstName = null,
  lastName = null,
}) {
  const theme = useTheme();
  const name = [];
  if (firstName) {
    name.push(firstName);
  }
  if (lastName) {
    name.push(lastName);
  }

  const properName = name.join(' ');

  function getInitials(nameArg = '') {
    return nameArg
      .trim()
      .split(/\s+/) // split by one or more spaces
      .slice(0, 2) // take first 2 words
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }

  const config = {
    AGENT: {
      color: theme.palette.warning.main,
      text: 'Service Provider',
      side: 'right',
    },
    ADMIN: {
      color: theme.palette.primary.main,
      text: 'Service Administrator',
      side: 'right',
    },
    SYSTEM: {
      color: theme.palette.primary.main,
      text: 'System',
      side: 'right',
    },
    CUSTOMER: {
      color: theme.palette.success.main,
      text: 'Customer',
      side: 'left',
    },
  };

  const isLeft = config?.[from]?.side === 'left';

  const chatWidthPercent = '60%';

  const blockStyle = {
    width: chatWidthPercent,
    minWidth: chatWidthPercent,
    mb: 2,
    ...(!isLeft && { marginLeft: 'auto' }),
  };
  const isFailedToDeliverMessage = status?.toLowerCase() === 'failed';

  return (
    <Box sx={blockStyle}>
      <Stack gap={0.5} direction="row" alignItems="center">
        {isLeft && (
          <UserAvatar
            properName={properName}
            initials={getInitials(properName)}
            config={config}
            from={from}
          />
        )}
        <Stack sx={{ width: '100%' }}>
          <Box sx={{ position: 'relative' }}>
            {/* Ribbon for type */}
            {type && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: -8,
                  zIndex: 2,
                  bgcolor: isFailedToDeliverMessage
                    ? 'error.main'
                    : 'primary.main',
                  color: '#fff',
                  px: 1.5,
                  py: 0.3,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderTopLeftRadius: '6px',
                  borderBottomRightRadius: '6px',
                  boxShadow: 2,
                  pointerEvents: 'none',
                }}>
                {type} {isFailedToDeliverMessage ? '( Failed )' : ''}
              </Box>
            )}
            <Card
              variant="outlined"
              sx={{
                p: 1,
                pb: 0.2,
                mt: 2.5,
                boxShadow: 1,
              }}>
              <CardContent
                sx={{
                  px: 1,
                  py: 0.5,
                  whiteSpace: 'pre-wrap',
                  ':last-child': {
                    pb: 1,
                  },
                }}>
                <Box>{message}</Box>
                <Box>
                  <Grid2 container sx={{ pt: 2 }}>
                    <Grid2 xs={8}>
                      <Chip
                        sx={{ border: 0, fontSize: '0.9em', fontWeight: 500 }}
                        label={
                          <FromChip
                            from={from}
                            config={config}
                            properName={properName}
                          />
                        }
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                      <Chip
                        sx={{ border: 0 }}
                        label={toLongDate(createdTime)}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    </Grid2>
                    <Grid2 xs={4} sx={{ textAlign: 'right' }}>
                      <Chip
                        sx={{
                          ml: 1,
                          border: 0,
                          fontSize: '0.9em',
                          fontWeight: 500,
                        }}
                        label={`To: ${config[to].text}`}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    </Grid2>
                  </Grid2>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Stack>
        {!isLeft && (
          <UserAvatar
            properName={properName}
            initials={getInitials(properName)}
            config={config}
            from={from}
          />
        )}
      </Stack>
    </Box>
  );
}

function Conversations({ conversationData, defaultMessage, onChange, limit }) {
  const hasConversations =
    Array.isArray(conversationData?.data) && conversationData?.data?.length > 0;

  if (!hasConversations) {
    return <Typography sx={{ m: 2 }}>{defaultMessage}</Typography>;
  }

  const pageCount = Math.ceil(conversationData.total / limit);

  return (
    <Box>
      {conversationData?.data?.map(c => (
        <Conversation
          key={c?.id}
          message={c?.content}
          from={c?.from}
          type={c?.type}
          to={c?.to}
          status={c?.status}
          createdTime={c?.createdDateTime}
          side={c?.direction === 'INBOUND' ? 'left' : 'right'}
          firstName={c?.sender?.name || c?.recipient?.name}
          lastName=""
        />
      ))}
      <Divider />
      {pageCount > 1 && (
        <Box sx={{ mt: 2 }} display="flex" justifyContent="center">
          <Pagination
            page={conversationData?.page || 1}
            onChange={async (event, value) => {
              onChange(value);
            }}
            sx={{
              mt: 1,
              mr: 1,
              ml: 1,
              justifyContent: 'flex-end',
              display: 'flex',
            }}
            count={pageCount}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}

function AiChat({ serviceJobData }) {
  const limit = 5;

  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const defaultMessage = formatMessage({
    id: 'NO_CONVERSATION_EXISTS',
    defaultMessage: 'No conversations to display',
  });

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const [loading, setLoading] = useState(true);
  const [conversationData, setConversationData] = useState(null);

  const fetchConversations = async (page = 1) => {
    const url = `${getApiUrl()}/sxcl/v1/insights/communication/${serviceJobData.id}?page=${page}&limit=${limit}`;
    const response = await get(url);
    if (response?.ok) {
      return response?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const initialize = async (page = 1) => {
    setLoading(true);

    const conversationsFound = await fetchConversations(page);
    if (conversationsFound) {
      setConversationData(conversationsFound);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (serviceJobData?.id) {
      initialize();
    } else {
      setLoading(false);
    }
  }, [serviceJobData?.id]);

  return (
    <Grid2>
      {loading ? (
        <Box textAlign="center" sx={{ height: '120px' }} alignContent="center">
          <AiLoader>Loading Chats...</AiLoader>
          <Typography color="primary.main">Loading</Typography>
        </Box>
      ) : (
        <Box>
          <Conversations
            conversationData={conversationData}
            defaultMessage={defaultMessage}
            onChange={page => {
              initialize(page);
            }}
            limit={limit}
          />
        </Box>
      )}
    </Grid2>
  );
}

export default AiChat;
